"use client";

import type { MotionValue } from "motion/react";
import { useLocale } from "next-intl";
import { useTheme } from "next-themes";
import dynamic from "next/dynamic";
import {
  Component,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

import { CardFallback } from "./card-fallback";

// =============================================================================

const loadCanvas = () => import("./card-canvas");
const CardCanvas = dynamic(loadCanvas, { ssr: false });

/** Runs a task once the page is idle */
function whenIdle(task: () => void): () => void {
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(task, { timeout: 5000 });
    return () => window.cancelIdleCallback(id);
  }
  const timeout = setTimeout(task, 2500);
  return () => clearTimeout(timeout);
}

/** No GPU or blocklisted driver: the CSS card does better */
const SOFTWARE_RENDERERS =
  /swiftshader|llvmpipe|softpipe|software|basic render/i;

let webglSupport: boolean | undefined;

/**
 * Hardware accelerated WebGL 2, probed once with a throwaway context (as
 * costly as the real one, so the probe runs when the page is idle).
 */
function getWebglSupport(): boolean {
  if (webglSupport === undefined) {
    try {
      const context = document
        .createElement("canvas")
        .getContext("webgl2", { failIfMajorPerformanceCaveat: true });
      const info = context?.getExtension("WEBGL_debug_renderer_info");
      const renderer = info
        ? String(context?.getParameter(info.UNMASKED_RENDERER_WEBGL))
        : "";
      webglSupport = context !== null && !SOFTWARE_RENDERERS.test(renderer);
      context?.getExtension("WEBGL_lose_context")?.loseContext();
    } catch {
      webglSupport = false;
    }
  }
  return webglSupport;
}

/** The CSS card takes over if the WebGL scene throws */
class CanvasBoundary extends Component<
  { children: ReactNode; onError: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// =============================================================================

type CardStageProps = {
  progress: MotionValue<number>;
  role: string;
  tags: string[];
  qrSvg: string;
  /** Accessible description of the card */
  label: string;
  className?: string;
};

/**
 * Business card of the "Still here?" section. WebGL is probed and the 3D
 * chunk downloaded at idle time six screens ahead, the WebGL card is mounted
 * three screens ahead; the CSS card stands in until its first frame (and for
 * good without hardware WebGL).
 */
export function CardStage({
  progress,
  role,
  tags,
  qrSvg,
  label,
  className,
}: CardStageProps) {
  const locale = useLocale();
  const { resolvedTheme } = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fallbackHidden, setFallbackHidden] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // Far ahead, at idle time: probe WebGL, then download and evaluate the 3D
    // chunk (three.js is heavy: kept out of the first load of the page)
    let cancelIdle: (() => void) | undefined;
    const preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        preloadObserver.disconnect();
        cancelIdle = whenIdle(() => {
          if (getWebglSupport()) void loadCanvas();
        });
      },
      { rootMargin: "600% 0px" }
    );
    // Mounted a few screens ahead: ready before the card shows up
    const nearObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setNear(true);
        nearObserver.disconnect();
      },
      { rootMargin: "300% 0px" }
    );
    const screenObserver = new IntersectionObserver(([entry]) =>
      setOnScreen(entry.isIntersecting)
    );
    preloadObserver.observe(element);
    nearObserver.observe(element);
    screenObserver.observe(element);
    return () => {
      cancelIdle?.();
      preloadObserver.disconnect();
      nearObserver.disconnect();
      screenObserver.disconnect();
    };
  }, []);

  const handleReady = useCallback(() => setReady(true), []);
  const handleFailure = useCallback(() => {
    setFailed(true);
    setFallbackHidden(false);
  }, []);

  // Probed at idle time, or now if the section is reached first
  const use3d = near && !failed && getWebglSupport();
  const show3d = use3d && ready;

  return (
    <div
      ref={ref}
      role="img"
      aria-label={label}
      className={cn("pointer-events-none absolute inset-0", className)}
    >
      {!(show3d && fallbackHidden) && (
        <CardFallback
          progress={progress}
          role={role}
          tags={tags}
          qrSvg={qrSvg}
          className={cn(
            "transition-opacity duration-500",
            show3d && "opacity-0"
          )}
          onTransitionEnd={(event) => {
            if (show3d && event.target === event.currentTarget) {
              setFallbackHidden(true);
            }
          }}
        />
      )}
      {use3d && (
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-500",
            show3d ? "opacity-100" : "opacity-0"
          )}
        >
          <CanvasBoundary onError={handleFailure}>
            <CardCanvas
              progress={progress}
              locale={locale}
              theme={resolvedTheme === "light" ? "light" : "dark"}
              active={onScreen}
              onReady={handleReady}
              onFailure={handleFailure}
            />
          </CanvasBoundary>
        </div>
      )}
    </div>
  );
}

// =============================================================================
